import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  memo,
} from "react";
import {
  Paper,
  Button,
  Box,
  Typography,
  CircularProgress,
  Chip,
  debounce,
  Menu,
  TextField,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { fetchLeaderboardData } from "../../../action/leaderboardActionUser";
import {
  getUserAppConfiguration,
  mapDefaultLeaderboardFilter,
} from "../../../api/appConfiguration";
import { toast } from "react-toastify";
import { isEmptyArray } from "formik";
import { isArray } from "jquery";
import GoalVideoComponent from "./GoalVideoComponent";
import VideoSkeleton from "./VideoSkeleton";
import { useParams } from "react-router-dom";
import moment from "moment";
import { YouTube } from "@mui/icons-material";
import { useSkipFirstRender } from "../../../hooks/useSkipFirstRender";
import {
  getVotingSeasonEndYear,
  getSeasonOptions,
} from "../../../utils/footballSeason";
import Background from "../../../assets/customerAssets/gradient.png";
import Position from "./leaderboardBanner/Position";
import CategoryComponent from "../CategoryComponent/Index";

const CustomTextarea = styled(TextField)(() => ({
  flexGrow: "1",
  justifyContent: "center",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    color: "#6f7081",
    padding: "0px 0px 0px 30px;",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&.Mui-focused": {
      backgroundColor: "transparent",
      outline: "none",
    },
  },

  "& .MuiInputBase-input": {
    padding: "0",
  },

  "& .MuiSelect-icon": {
    color: "#6f7081 !important",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
    "&:hover": {
      border: "none",
    },
  },
}));

const active = {
  color: "#d93349",
  borderBottom: "2px solid #d93349",
  flexGrow: "1",
  minHeight: "50px",
};

const GoalVideos = () => {
  const token = useSelector((state) => state.auth.token);
  const leaderboardData = useSelector((state) => state.leaderboardUser);
  const [leaderboard, setLeaderboard] = useState(null);
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("");
  const goalCategories = useSelector((state) => state.goalCategories);

  // const [page, setPage] = useState(1);
  const page = useRef(1);
  const bannerRef = useRef(null);
  const [leaderboardFilter, setLeaderboardFilter] = useState(null);
  const initialLoadDone = useRef(false);
  // const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [month, setMonth] = useState(moment().format("M-YYYY"));
  const [year, setYear] = useState(() => String(getVotingSeasonEndYear()));
  const seasonOptions = useMemo(() => getSeasonOptions(5), []);
  const globalSearchData = useSelector((state) => state.globalSearch);
  const { id, type } = useParams();
  const [subFilter, setSubFilter] = useState("");
  const [childFilter, setChildFilter] = useState("");
  const [bannerHovered, setBannerHovered] = useState(false);
  const fetchSearchResult = useMemo(
    () =>
      debounce((key, api, data) => {
        if (api == "leaderboards" && data?.length > 0) {
          setLoading(() => true);
          if (key !== "") {
            const re = new RegExp(key, "i");
            setSearchData(() => {
              const searchedData = data.filter(
                (data) =>
                  data.title.match(re) ||
                  data.club.match(re) ||
                  data.goal_scorer.match(re) ||
                  data.region.match(re)
              );

              return searchedData;
            });
          } else {
            setSearchData([]);
          }
          setLoading(() => false);
        } else {
        }
      }, 200),
    []
  );

  const clearLeaderboardView = useCallback(() => {
    dispatch({ type: "EMPTY_LEADERBOARD_STATE" });
    setLeaderboard(null);
    setSearchData([]);
    setLoading(true);
  }, [dispatch]);

  const loadLeaderboard = useCallback(
    (filterType = leaderboardFilter) => {
      if (!token || !filterType) return;

      if (filterType === "monthly") {
        dispatch(
          fetchLeaderboardData(
            token,
            filter,
            "monthly",
            {
              month: month?.split("-")[0],
              year: month?.split("-")[1],
            },
            { subFilter, childFilter }
          )
        );
      } else {
        dispatch(
          fetchLeaderboardData(
            token,
            filter,
            "yearly",
            { year },
            { subFilter, childFilter }
          )
        );
      }
    },
    [token, filter, month, year, subFilter, childFilter, leaderboardFilter, dispatch]
  );

  const switchLeaderboardTab = useCallback(
    (nextFilter) => {
      if (leaderboardFilter === nextFilter) return;
      clearLeaderboardView();
      setLeaderboardFilter(nextFilter);
    },
    [leaderboardFilter, clearLeaderboardView]
  );

  useEffect(() => {
    if (leaderboardData.data?.data?.length > 0) {
      fetchSearchResult(
        globalSearchData.search,
        globalSearchData.api,
        leaderboardData.data?.data
      );
    } else {
    }
  }, [globalSearchData]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const resolveDefaultFilter = async () => {
      if (type === "monthly") return "monthly";
      if (type === "seasons") return "yearly";

      try {
        const data = await getUserAppConfiguration(token);
        if (data?.success) {
          return mapDefaultLeaderboardFilter(data.data?.default_leaderboard);
        }
      } catch (_) {}

      return "monthly";
    };

    (async () => {
      const defaultFilter = await resolveDefaultFilter();
      if (cancelled) return;

      setLeaderboardFilter(defaultFilter);
      clearLeaderboardView();
      initialLoadDone.current = true;

      if (defaultFilter === "monthly") {
        dispatch(
          fetchLeaderboardData(token, filter, "monthly", {
            month: month?.split("-")[0],
            year: month?.split("-")[1],
          })
        );
      } else {
        dispatch(fetchLeaderboardData(token, filter, "yearly", { year }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  useSkipFirstRender(() => {
    if (token && month) {
      clearLeaderboardView();
      loadLeaderboard("monthly");
    }
  }, [month]);

  useSkipFirstRender(() => {
    if (token && Number(year)) {
      clearLeaderboardView();
      loadLeaderboard("yearly");
    }
  }, [year]);

  useSkipFirstRender(() => {
    if (token) {
      clearLeaderboardView();
      loadLeaderboard();
    }
  }, [filter]);

  useSkipFirstRender(() => {
    if (token && subFilter) {
      clearLeaderboardView();
      loadLeaderboard();
    }
  }, [subFilter]);

  useSkipFirstRender(() => {
    if (token && childFilter) {
      clearLeaderboardView();
      loadLeaderboard();
    }
  }, [childFilter]);

  useSkipFirstRender(() => {
    if (!token || !initialLoadDone.current) return;
    clearLeaderboardView();
    loadLeaderboard(leaderboardFilter);
  }, [leaderboardFilter]);

  useEffect(() => {
    if (leaderboardData.data?.success) {
      if (leaderboardData.data?.data?.length > 0) {
        setLeaderboard(() =>
          leaderboardData?.data?.data.map((data, index) => ({
            rank: index + 1,
            ...data,
          }))
        );
        setLoading(false);
      } else {
        setLeaderboard("no data found");
        setLoading(false);
      }
    }
    if (leaderboardData.error) {
      toast.error("error in leaderboard data" + leaderboardData.error);
      setLoading(false);
    }
  }, [leaderboardData]);

  useEffect(() => {
    if (id) {
      const queryCat = goalCategories.data?.data?.find((cat) => cat.name == id);
      if (queryCat) {
        setFilter(queryCat.id);
      }

      if (type == "monthly") {
        switchLeaderboardTab("monthly");
      } else if (type == "seasons") {
        switchLeaderboardTab("yearly");
      }
    }
  }, [id, type, goalCategories.data?.data, switchLeaderboardTab]);

  // useEffect(() => {
  //   console.log(
  //     "loading ********************&&&&&&&&&&&&&&&&&&&&&&&&&&&&&&",
  //     loading,
  //   );
  // }, [loading]);

  // "";

  return (
    <>
      <Box className="leader-board-main">
        <Box className="leader-board-wrapper">
          <Box className="heading">
            <Typography component="h1">Leaderboards</Typography>
          </Box>
          <Box className="month-filter-wrapper">
            <Button
              className="monthly"
              sx={
                leaderboardFilter == "monthly"
                  ? active
                  : { flexGrow: "1", color: "white", minHeight: "50px" }
              }
              onClick={() => {
                switchLeaderboardTab("monthly");
              }}
            >
              Monthly
            </Button>

            <Button
              className="monthly"
              sx={
                leaderboardFilter == "yearly"
                  ? active
                  : { flexGrow: "1", color: "white", minHeight: "50px" }
              }
              onClick={() => {
                switchLeaderboardTab("yearly");
              }}
            >
              Seasons
            </Button>
          </Box>

          {goalCategories.loading ? (
            <CircularProgress />
          ) : Array.isArray(goalCategories?.data?.data) ? (
            <CategoryComponent
              categories={goalCategories.data.data}
              filter={filter}
              setFilter={setFilter}
              subFilter={subFilter}
              setSubFilter={setSubFilter}
              childFilter={childFilter}
              setChildFilter={setChildFilter}
              page={"MyAccount"}
            />
          ) : (
            "No category data found"
          )}

          <Box
            className="Category-filter-wrapper"
            sx={{ justifyContent: "space-between" }}
          >
            {leaderboardFilter == "yearly" ? (
              <CustomTextarea
                select
                variant="outlined"
                name="previous_months"
                value={year}
                onChange={(e) => {
                  setYear(e.target.value);
                }}
                // className={month !== "default" ? "monthly active" : "monthly"}
                sx={{
                  maxWidth: "200px",
                  border: "1px solid #414466",
                  fontSize: { xs: "14px", md: "16px" },
                }}
              >
                <MenuItem value="default">Select Year</MenuItem>
                {seasonOptions.map((season) => (
                  <MenuItem key={season.endYear} value={season.endYear}>
                    {season.label}
                  </MenuItem>
                ))}
              </CustomTextarea>
            ) : (
              <CustomTextarea
                select
                variant="outlined"
                name="previous_months"
                value={month}
                onChange={(e) => {
                  setMonth(e.target.value);
                }}
                sx={{
                  maxWidth: "200px",
                  border: "1px solid #414466",
                  borderRadius: "8px",
                  fontSize: { xs: "14px", md: "16px" },
                }}
              >
                <MenuItem value="default">Select Months</MenuItem>
                {Array.from({ length: 6 }).map((_, i) => (
                  <MenuItem
                    value={moment().subtract(i, "months").format("M-YYYY")}
                  >
                    {moment().subtract(i, "months").format("MMM-YYYY")}
                  </MenuItem>
                ))}
              </CustomTextarea>
            )}

            <Button
              sx={{ color: "#d93349" }}
              onClick={() => {
                window.open("https://www.youtube.com/watch?v=OsOx-q5Z6qw");
              }}
            >
              <YouTube sx={{ height: "40px", width: "40px" }} />
            </Button>
          </Box>

          {!loading && Array.isArray(leaderboard) && leaderboard.length > 0 ? (
            <Box
              className="leaderboard-banner-wrapper"
              ref={bannerRef}
              onMouseEnter={() => setBannerHovered(true)}
              onMouseLeave={() => setBannerHovered(false)}
              sx={{
                position: "relative",
                overflow: "hidden",
                cursor: "default",
              }}
            >
              <Box className="background">
                <Typography component={"span"} className="heading_text">
                  TOP LEADERBOARD OF THE{" "}
                  {leaderboardFilter == "yearly" ? "SEASON" : "MONTH"}
                </Typography>
                <Box className="position-wrapper">
                  <Position
                    index={0}
                    leaderboard={leaderboard}
                    bannerRef={bannerRef}
                    bannerHovered={bannerHovered}
                  />
                  {leaderboard.length > 1 && (
                    <Position
                      index={1}
                      leaderboard={leaderboard}
                      bannerRef={bannerRef}
                      bannerHovered={bannerHovered}
                    />
                  )}
                  {leaderboard.length > 2 && (
                    <Position
                      index={2}
                      leaderboard={leaderboard}
                      bannerRef={bannerRef}
                      bannerHovered={bannerHovered}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          ) : null}

          <Box className="leaderboard-data-wrapper">
            {loading || leaderboardFilter == null ? (
              Array.from({ length: 10 }).map((ele) => <VideoSkeleton />)
            ) : // searchData.length > 0 ||
            globalSearchData.search.trim() !== "" ? (
              <>
                <Box className="rank-logo">Rank</Box>
                {searchData.length > 0 ? (
                  searchData.map((ele, index) => {
                    if (index == searchData.length - 1) {
                      return (
                        <>
                          <GoalVideoComponent
                            key={index}
                            data={ele}
                            setLeaderboard={setLeaderboard}
                          />
                        </>
                      );
                    } else {
                      return (
                        <>
                          <GoalVideoComponent
                            key={index}
                            data={ele}
                            setLeaderboard={setLeaderboard}
                          />
                        </>
                      );
                    }
                  })
                ) : (
                  <Box className="no-data-found">Empty search results</Box>
                )}
              </>
            ) : isArray(leaderboard) && leaderboard.length > 0 ? (
              <>
                <Box className="rank-logo">Rank</Box>
                {leaderboard.map((ele, index) => {
                  if (index == leaderboard.length - 1) {
                    return (
                      <>
                        <GoalVideoComponent
                          key={index}
                          data={ele}
                          setLeaderboard={setLeaderboard}
                        />
                      </>
                    );
                  } else {
                    return (
                      <>
                        <GoalVideoComponent
                          key={index}
                          data={ele}
                          setLeaderboard={setLeaderboard}
                        />
                      </>
                    );
                  }
                })}
              </>
            ) : (
              <Box className="no-data-found">No Data Found</Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default GoalVideos;
